'use client';

import { c as _c } from "react/compiler-runtime";
import { createLogger } from '@svs/nova-core/log';
import { useAccountBalance } from '@svs/nova-core/nextjs/auth';
import { createMutationFn, wagerMutationKeys } from '../../../../lib/entry.js';
import { useMutation } from '../../../../tanstack/entry.js';
import { placeExternalCouponWager } from '../../../actions/coupon/placeExternalCouponWager.js';
import { updateExternalWager } from '../../../actions/wager/updateExternalWager.js';
import { useRemovePaidActivatedDrawIdFromUrl } from './useRemovePaidActivatedDrawIdFromUrl.js';
const mutationFn = createMutationFn(placeExternalCouponWager);
const updateExternalWagerMutationFn = createMutationFn(updateExternalWager);
const logger = createLogger('poolbetting.place-external-coupon-wager');
/**
 * React hook to place an external coupon wager and refresh account balance on success.
 *
 * @param root0 hook parameters
 * @param root0.activatedDrawId - Optional activated draw ID, used for marketplace wagers.
 * @param root0.paymentUrls - Optional payment URLs, used for redirecting in payment middlewares.
 * @param root0.marketplaceWallets - Optional marketplace wallet information, used for marketplace wagers to determine if the wager can be placed and to refresh balance after payment.
 * @param root0.callbackOnPaymentMpShares - Optional callback function to be called after payment of marketplace shares.
 * @param root0.callbackOnPaymentDone - Optional callback function to be called after payment is done.
 * @param root0.externalWagerGuid - Optional external wager GUID, used for updating the external wager after successful placement.
 * @param root0.role - User role, used to determine if the user is a player or retailer.
 * @param root0.trackTransaction - Function to track ecommerce analytics events, used for tracking payment events.
 * @param root0.trackEventFunction - Function to track general analytics events, used for tracking various user interactions.
 * @returns Mutation result for placing an external coupon wager.
 */
export function usePlaceExternalCouponWager(t0) {
  const $ = _c(23);
  const {
    activatedDrawId,
    paymentUrls,
    marketplaceWallets,
    callbackOnPaymentMpShares,
    callbackOnPaymentDone,
    externalWagerGuid,
    role,
    trackTransaction,
    trackEventFunction
  } = t0;
  const isPlayer = role === "Player";
  const {
    forceRefresh
  } = useAccountBalance();
  const removePaidActivatedDrawId = useRemovePaidActivatedDrawIdFromUrl();
  let t1;
  if ($[0] !== activatedDrawId) {
    t1 = wagerMutationKeys.placeExternalCouponWager(activatedDrawId);
    $[0] = activatedDrawId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== activatedDrawId || $[3] !== marketplaceWallets || $[4] !== paymentUrls || $[5] !== role || $[6] !== trackEventFunction || $[7] !== trackTransaction) {
    t2 = async props => mutationFn(props, {
      role: role || "Player",
      activatedDrawId,
      paymentUrls,
      marketplaceWallets,
      trackTransaction,
      trackEventFunction
    });
    $[2] = activatedDrawId;
    $[3] = marketplaceWallets;
    $[4] = paymentUrls;
    $[5] = role;
    $[6] = trackEventFunction;
    $[7] = trackTransaction;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== callbackOnPaymentDone || $[10] !== externalWagerGuid || $[11] !== forceRefresh || $[12] !== isPlayer || $[13] !== removePaidActivatedDrawId) {
    t3 = async data => {
      if (externalWagerGuid) {
        ;
        try {
          await updateExternalWagerMutationFn({
            externalWagerGuid,
            transactionIds: data.transactionIds
          });
        } catch (t4) {
          const error = t4;
          logger.warn("Failed to update external wager after successful payment", {
            error,
            externalWagerGuid
          });
        }
      }
      if (callbackOnPaymentDone) {
        callbackOnPaymentDone();
      }
      removePaidActivatedDrawId(data.paidActivatedDrawIds);
      if (isPlayer) {
        forceRefresh();
      }
    };
    $[9] = callbackOnPaymentDone;
    $[10] = externalWagerGuid;
    $[11] = forceRefresh;
    $[12] = isPlayer;
    $[13] = removePaidActivatedDrawId;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== callbackOnPaymentDone || $[16] !== callbackOnPaymentMpShares) {
    t4 = error_0 => {
      if (callbackOnPaymentDone) {
        callbackOnPaymentDone();
      }
      if (error_0.cause && error_0.cause === "PAYMENT_ABORTED_MP_SHARES" && callbackOnPaymentMpShares) {
        callbackOnPaymentMpShares();
      }
    };
    $[15] = callbackOnPaymentDone;
    $[16] = callbackOnPaymentMpShares;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4) {
    t5 = {
      mutationKey: t1,
      mutationFn: t2,
      onSuccess: t3,
      onError: t4
    };
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  return useMutation(t5);
}